import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { ToastContainer, ToastType } from '../components/Toast';

interface Toast {
  id: string;
  type: ToastType;
  title: string;
  message?: string;
  duration?: number;
  icon?: string;
}

interface ToastOptions {
  id?: string;
  duration?: number;
  icon?: string;
}

interface ToastContextType {
  success: (title: string, messageOrOptions?: string | ToastOptions, duration?: number) => string;
  error: (title: string, messageOrOptions?: string | ToastOptions, duration?: number) => string;
  warning: (title: string, messageOrOptions?: string | ToastOptions, duration?: number) => string;
  info: (title: string, messageOrOptions?: string | ToastOptions, duration?: number) => string;
  loading: (title: string, options?: ToastOptions) => string;
  dismiss: (id: string) => void;
}

// Durate di default per tipo (corrispondono ai valori precedenti di react-hot-toast Toaster)
const DEFAULT_DURATIONS: Record<ToastType, number> = {
  success: 3000,
  error: 5000,
  warning: 4000,
  info: 4000,
  loading: 0, // loading non si chiude da solo
};

// Estratto fuori dal componente per stabilità referenziale
const parseArgs = (
  messageOrOptions?: string | ToastOptions,
  duration?: number
): { message?: string; options: ToastOptions } => {
  if (typeof messageOrOptions === 'object' && messageOrOptions !== null) {
    return { message: undefined, options: messageOrOptions };
  }
  return {
    message: messageOrOptions,
    options: { duration },
  };
};

const generateId = () => Math.random().toString(36).substring(2, 11);

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

interface ToastProviderProps {
  children: React.ReactNode;
}

export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const addOrUpdateToast = useCallback((toast: Toast) => {
    setToasts(prev => {
      const existingIndex = prev.findIndex(t => t.id === toast.id);
      if (existingIndex >= 0) {
        // Aggiorna toast esistente (es. loading → success)
        const updated = [...prev];
        updated[existingIndex] = toast;
        return updated;
      }
      return [...prev, toast];
    });
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  const createToast = useCallback((
    type: ToastType,
    title: string,
    messageOrOptions?: string | ToastOptions,
    duration?: number
  ): string => {
    const { message, options } = parseArgs(messageOrOptions, duration);
    const id = options.id || generateId();
    const finalDuration = options.duration ?? duration ?? DEFAULT_DURATIONS[type];
    addOrUpdateToast({
      id,
      type,
      title,
      message,
      duration: finalDuration,
      icon: options.icon,
    });
    return id;
  }, [addOrUpdateToast]);

  const success = useCallback((title: string, messageOrOptions?: string | ToastOptions, duration?: number) => {
    return createToast('success', title, messageOrOptions, duration);
  }, [createToast]);

  const error = useCallback((title: string, messageOrOptions?: string | ToastOptions, duration?: number) => {
    return createToast('error', title, messageOrOptions, duration);
  }, [createToast]);

  const warning = useCallback((title: string, messageOrOptions?: string | ToastOptions, duration?: number) => {
    return createToast('warning', title, messageOrOptions, duration);
  }, [createToast]);

  const info = useCallback((title: string, messageOrOptions?: string | ToastOptions, duration?: number) => {
    return createToast('info', title, messageOrOptions, duration);
  }, [createToast]);

  const loading = useCallback((title: string, options?: ToastOptions) => {
    const id = options?.id || generateId();
    addOrUpdateToast({
      id,
      type: 'loading',
      title,
      duration: 0,
      icon: options?.icon,
    });
    return id;
  }, [addOrUpdateToast]);

  const dismiss = useCallback((id: string) => {
    removeToast(id);
  }, [removeToast]);

  // Il valore del contesto DEVE essere memoizzato: le funzioni qui sopra sono gia'
  // stabili (useCallback), ma un oggetto letterale sarebbe nuovo a ogni render del
  // provider. Chi mette il toast fra le dipendenze di un useCallback/useEffect si
  // ritroverebbe l'effetto rieseguito a ogni toast mostrato: se quell'effetto e' una
  // chiamata che fallisce e mostra un toast d'errore, il ciclo non si ferma piu'.
  const value = useMemo(
    () => ({ success, error, warning, info, loading, dismiss }),
    [success, error, warning, info, loading, dismiss],
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </ToastContext.Provider>
  );
};
